import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, get, post, qs, type Booking, type CustomerLookup, type Lookups, type LookupOption } from '../lib/api.ts';
import { useOptionalSession } from '../auth/session.tsx';
import { OPERATION_TYPES } from '../lib/labels.ts';
import { DateInput, Alert, Field, Select, TextInput } from '../components/ui.tsx';
import { BK } from './fields.ts';
import { cairoToday, displayPhone, isWholeEGP, shortDate, toWholeInput } from '../lib/format.ts';
import { AltPhoneField } from '../components/AltPhoneField.tsx';

const opt = (o: LookupOption) => ({ code: o.code, ar: o.labelAr });
// القاعدة المعتمدة: المبالغ بالجنيه الصحيح — لا كسور في الإدخال (الفحص المشترك في isWholeEGP).
const MONEY_ERROR = 'مبلغ غير صحيح — اكتب رقمًا صحيحًا بالجنيه بلا كسور';
const needsRelation = (op: string) => op === 'MODIFICATION' || op === 'CANCELLATION';

export interface Draft {
  customerName: string; phone: string; altPhone: string; dateOfBirth: string; companyName: string;
  bookingType: string; hotelOrVisa: string; nationality: string;
  fromCity: string; transitCity: string; toCity: string; adults: string; children: string; infants: string;
  departureDate: string; returnDate: string; salePrice: string; costPrice: string;
  // حجز الفندق (0051): تكلفة الفندق + كراسي الباص × تكلفة الكرسي = التكلفة الإجمالية (على الخادم)
  hotelCost: string; busSeats: string; busSeatCost: string;
  executionSource: string; customerRating: string; operationType: string;
  // مصدر التنفيذ لكل جزء في الحجز المركّب (0124، قرار المالك 23/09/2026)
  executionSourceHotel: string; executionSourceFlight: string; executionSourceVisa: string;
  relatedBookingNo: string; externalReference: string;
}

export const emptyDraft = (): Draft => ({
  customerName: '', phone: '', altPhone: '', dateOfBirth: '', companyName: '',
  bookingType: '', hotelOrVisa: '', nationality: '',
  fromCity: '', transitCity: '', toCity: '', adults: '1', children: '0', infants: '0',
  departureDate: '', returnDate: '', salePrice: '', costPrice: '',
  hotelCost: '', busSeats: '', busSeatCost: '',
  executionSource: '', customerRating: 'NOT_DONE', operationType: 'NEW',
  executionSourceHotel: '', executionSourceFlight: '', executionSourceVisa: '',
  relatedBookingNo: '', externalReference: '',
});

export const draftOf = (b: Booking): Draft => ({
  ...emptyDraft(),
  customerName: b.customerName ?? '', phone: b.phone ? displayPhone(b.phone) : '', altPhone: b.altPhone ? displayPhone(b.altPhone) : '',
  dateOfBirth: b.dateOfBirth ?? '', companyName: b.companyName ?? '', bookingType: b.bookingType ?? '',
  hotelOrVisa: b.hotelOrVisa ?? '', nationality: b.nationality ?? '', fromCity: b.fromCity ?? '',
  transitCity: b.transitCity ?? '', toCity: b.toCity ?? '',
  adults: String(b.adults ?? 1), children: String(b.children ?? 0), infants: String(b.infants ?? 0),
  departureDate: b.departureDate ?? '', returnDate: b.returnDate ?? '',
  salePrice: toWholeInput(b.salePrice), costPrice: toWholeInput(b.costPrice),
  hotelCost: toWholeInput(b.hotelCost), busSeats: b.busSeats === null || b.busSeats === undefined ? '' : String(b.busSeats),
  busSeatCost: toWholeInput(b.busSeatCost),
  executionSource: b.executionSource ?? '', customerRating: b.customerRating ?? '',
  executionSourceHotel: b.executionSourceHotel ?? '', executionSourceFlight: b.executionSourceFlight ?? '',
  executionSourceVisa: b.executionSourceVisa ?? '',
  operationType: b.operationType ?? 'NEW',
  relatedBookingNo: b.relatedBookingNo ?? '', externalReference: b.externalReference ?? '',
});

/** قيمة محسوبة على الخادم — تُعرض فقط ولا تُحسب هنا أبدًا. */
function Computed({ label, value, suffix, hint }: { label: string; value: string | null | undefined; suffix?: string; hint?: string }) {
  const shown = value === null || value === undefined || value === '' ? '—' : `${value}${suffix ?? ''}`;
  return (
    <label className="block">
      <span className="label">{label}</span>
      <output className="control block bg-slate-50 tabular-nums text-ink-700">{shown}</output>
      {hint && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </label>
  );
}

export interface BookingFormProps {
  formLabel: string;
  /**
   * نصّ زرّ الحفظ — ودالّةٌ تأخذ المسودّة حين يتوقّف على ما فيها: الدفعة إجباريةٌ
   * في الجديد والمكرر فهي جزءٌ من «إنشاء الحجز» لا فعلٌ زائد عليه، وفي ما عداهما
   * اختيارٌ يُقال للبياع أنه سيُنفَّذ معه.
   */
  submitLabel: string | ((draft: Draft) => string);
  lookups: Lookups;
  salesName: string;
  isAdminUser: boolean;
  booking?: Booking | null;
  lockedPhone?: string | null;
  initial?: Partial<Draft>;
  /** أوّل تعديلٍ في النموذج — لتسأل النافذةُ قبل إغلاقها (02/10/2026) */
  onDirty?: () => void;
  readOnly?: boolean;
  readOnlyNote?: string | null;
  busy?: boolean;
  error?: string | null;
  banner?: ReactNode;
  /**
   * ما يُعرض أسفل الحقول — **دالّةٌ تأخذ المسودّة** لا عقدةً جامدة: قسم الدفع
   * يحتاج أن يعرف نوع العملية (ليُلزم الدفعة في الجديد والمكرر) وسعر البيع
   * (ليحسب المتبقي لحظةً بلحظة)، وكلاهما داخل هذا النموذج لا خارجه.
   */
  footer?: ReactNode | ((draft: Draft) => ReactNode);
  extraActions?: ReactNode;
  onSubmit: (body: Record<string, unknown>) => void | Promise<void>;
}

/**
 * نموذج الحجز المعتمد — الحقول 1→22 بالترتيب المعتمد حرفيًا، والحقول 17/18/19 تُعرض كما يرجعها الخادم.
 * الواجهة لا تحسب أي عمولة أو ضريبة أو ربح، ولا ترسل أي حقل محسوب إلى الخادم.
 */
/**
 * أنواع الحجز التي **فيها طيران** — وهي وحدها التي تُعرض معها خانات «من» و«مدينة
 * الترانزيت» و«الى». الرموز كما هي في `lookup_values` (BOOKING_TYPE).
 *
 * و«حجز مبدئى» منها صراحةً: المبدئي حجزُ مقعدٍ على خطّ بعينه بانتظار التأكيد، فخطّ سيره
 * هو أوّل ما يُكتب فيه — لا آخر ما يُكتب. وما عدا هذه الأنواع (فندق، تأشيرة، بار كود)
 * لا خطّ سير فيه أصلًا، فالخانات الثلاث تختفي بدل أن تبقى فارغة أو مقفلة بنصّ ثابت.
 */
export const FLIGHT_BOOKING_TYPES = ['PRELIMINARY', 'FLIGHT', 'HOTEL_FLIGHT', 'HOTEL_FLIGHT_VISA'] as const;
export const hasFlightLeg = (code: string) => (FLIGHT_BOOKING_TYPES as readonly string[]).includes(code);

/**
 * أنواع الحجز التي **فيها فندق أو تأشيرة** — وهي وحدها التي تُعرض معها خانة «اسم الفندق /
 * التاشيرة» (قرار المالك).
 *
 * فحجز الطيران وحده لا فندق فيه ولا تأشيرة، فالخانة معه فراغٌ يُملأ بـ«لا يوجد» في كل مرة
 * أو يُترك خاليًا — وكلاهما ضجيج. والمركّبات تُبقيها: «فندق + طيران» فيها فندق، و«فندق +
 * طيران + تأشيرة» فيها الاثنان.
 *
 * و«بار كود» و«حجز مبدئى» خارجها: لا فندق فيهما ولا تأشيرة.
 */
export const HOTEL_OR_VISA_BOOKING_TYPES = ['HOTEL', 'VISA', 'HOTEL_FLIGHT', 'HOTEL_FLIGHT_VISA'] as const;
export const hasHotelOrVisa = (code: string) => (HOTEL_OR_VISA_BOOKING_TYPES as readonly string[]).includes(code);

/**
 * أنواع الحجز التي **فيها فندق** (قرار المالك، 0051): معها تُكتب تكلفة الفندق وانتقالات
 * الباص (عدد الكراسي × تكلفة الكرسي)، والمجموع هو «التكلفة الإجمالية» — وهي «سعر التكلفة»
 * نفسه قبل ضريبة الـ٣٪ — يحسبه الخادم لا الواجهة. والقاعدة ترفض هذه الأعمدة في غير الفندق.
 */
export const HOTEL_BOOKING_TYPES = ['HOTEL', 'HOTEL_FLIGHT', 'HOTEL_FLIGHT_VISA'] as const;
export const hasHotel = (code: string) => (HOTEL_BOOKING_TYPES as readonly string[]).includes(code);

/**
 * الأنواع **المركّبة** (0124، قرار المالك 23/09/2026: «لو حد اختار فندق وطيران أو فندق وطيران وتأشيرة،
 * لازم مصدر التنفيذ لكل واحد يتحط علشان وأنا براجع»): بدل خانة «مصدر التنفيذ» الواحدة تُعرض خانةٌ
 * لكل جزء — الفندق والطيران، والتأشيرة إن كانت. والخادم والقاعدة يحرسان الشرط نفسه.
 */
export const COMBINED_BOOKING_TYPES = ['HOTEL_FLIGHT', 'HOTEL_FLIGHT_VISA'] as const;
export const isCombined = (code: string) => (COMBINED_BOOKING_TYPES as readonly string[]).includes(code);
/** الحجز المبدئي ومصدرُه الخاص «iCon ( حجز مبدئى )» (0129) — المصدر لا يصحّ إلا مع هذا النوع. */
export const PRELIMINARY = 'PRELIMINARY';
export const NEW_SOURCE = '__new_source';
const ICON_PRELIMINARY = 'ICON_PRELIMINARY';

/**
 * عناوين السعر والتكلفة في المركّب تقول ما يشمله الرقم (قرار المالك 23/09/2026: «في حالة الحجز فندق +
 * طيران خلّي خانة التكلفة اسمها: سعر التكلفة للفندق + الطيران، وسعر البيع (فندق + طيران)، وكذلك لو
 * ضفنا تأشيرة ضيف عليهم تأشيرة»). فالبياع يعرف أن الرقم للحزمة كلّها لا للفندق وحده.
 */
export function partsLabel(base: string, type: string): string {
  if (!isCombined(type)) return base;
  const visa = type === 'HOTEL_FLIGHT_VISA';
  if (base === BK.hotelCost) return `سعر التكلفة للفندق + الطيران${visa ? ' + التأشيرة' : ''}`;
  if (base === BK.salePrice) return `${base} (فندق + طيران${visa ? ' + تأشيرة' : ''})`;
  return base;
}

export function BookingForm(p: BookingFormProps) {
  const [f, setF] = useState<Draft>(() => ({ ...emptyDraft(), ...p.initial }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [resolving, setResolving] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  const set = (k: keyof Draft, v: string) => { setF(s => ({ ...s, [k]: v })); p.onDirty?.(); };
  /**
   * **«➕ مصدر جديد…» في قائمة مصدر التنفيذ — للمالك وحده** (قرار المالك 05/10/2026: «لو ف مصدر جديد اقدر انا فقط اضيفه من
   * هنا و ازود المصادر براحتى عادى بس انا بس ال اعمل كدا»): آخر خيارٍ في القائمة يفتح خانةً للاسم، و«إضافة» تحفظه على الخادم
   * (POST /admin/lookups/execution-sources) ويُختار فورًا في نفس الخانة ويظهر لكل الفريق من الحجز التالي.
   */
  const owner = useOptionalSession()?.me?.isOwner === true;
  type SourceField = 'executionSource' | 'executionSourceHotel' | 'executionSourceFlight' | 'executionSourceVisa';
  const [extraSources, setExtraSources] = useState<LookupOption[]>([]);
  const [addingFor, setAddingFor] = useState<SourceField | null>(null);
  const [newSource, setNewSource] = useState('');
  const [sourceBusy, setSourceBusy] = useState(false);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const pickSource = (k: SourceField, v: string) => { if (v === NEW_SOURCE) { setAddingFor(k); setNewSource(''); setSourceError(null); } else set(k, v); };
  async function addSource() {
    if (!addingFor || newSource.trim().length < 2) { setSourceError('اكتب اسم المصدر'); return; }
    setSourceBusy(true); setSourceError(null);
    try {
      const r = await post<LookupOption>('/admin/lookups/execution-sources', { labelAr: newSource.trim() });
      setExtraSources(x => [...x, r]); set(addingFor, r.code); setAddingFor(null); setNewSource('');
    } catch (e) { setSourceError(e instanceof ApiError ? e.message : 'تعذّر إضافة المصدر'); }
    finally { setSourceBusy(false); }
  }
  const newSourceBox = (k: SourceField) => addingFor === k && (
    <div className="mt-2 flex gap-2">
      <TextInput value={newSource} maxLength={80} aria-label="اسم المصدر الجديد" placeholder="مثلًا: شركة النيل" autoFocus
        onChange={e => setNewSource(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addSource(); } if (e.key === 'Escape') setAddingFor(null); }} />
      <button type="button" className="btn-primary shrink-0 px-3 py-1 text-sm" disabled={sourceBusy} onClick={() => void addSource()}>{sourceBusy ? '…' : 'إضافة'}</button>
      <button type="button" className="btn-ghost shrink-0 px-3 py-1 text-sm" disabled={sourceBusy} onClick={() => setAddingFor(null)}>إلغاء</button>
      {sourceError && <span className="self-center text-xs text-rose-700" role="alert">{sourceError}</span>}
    </div>
  );

  useEffect(() => {
    if (!f.bookingType && p.lookups.bookingTypes?.length) set('bookingType', p.lookups.bookingTypes[0].code);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [p.lookups]);
  useEffect(() => { first.current?.focus(); }, []);

  // تنظيف الخانات المخفيّة عند الانتقال إلى نوع يخفيها: خانةٌ لا يراها البياع لا يجوز أن
  // تُرسل قيمة — فحجز «تأشيرة» كان سيحمل مدينة قيامٍ كتبها وهو يظنّ الحجز طيرانًا، وحجز
  // «طيران» كان سيحمل اسم فندق كتبه قبل أن يبدّل النوع.
  // والتنظيف **عند التبديل وحده**: ما جاء من الخادم في حجز قائم لا يُمسّ لمجرّد فتحه.
  const prevType = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevType.current;
    prevType.current = f.bookingType;
    if (prev === null || prev === f.bookingType) return;
    const dropItinerary = !hasFlightLeg(f.bookingType);
    const dropHotel = !hasHotelOrVisa(f.bookingType);
    const dropHotelCost = !hasHotel(f.bookingType);
    setF(s2 => {
      // الحجز المبدئي تجهّزه الشركة: مصدره «iCon» يُختار وحده، ويُمحى إن تغيّر النوع (القاعدة ترفضه على غيره)
      if (f.bookingType === PRELIMINARY && !s2.executionSource) s2 = { ...s2, executionSource: ICON_PRELIMINARY };
      else if (f.bookingType !== PRELIMINARY && s2.executionSource === ICON_PRELIMINARY) s2 = { ...s2, executionSource: '' };
      const itinerary = dropItinerary && (s2.fromCity || s2.transitCity || s2.toCity);
      const hotel = dropHotel && s2.hotelOrVisa;
      const hotelCost = dropHotelCost && (s2.hotelCost || s2.busSeats || s2.busSeatCost);
      if (!itinerary && !hotel && !hotelCost) return s2;
      return {
        ...s2,
        ...(itinerary ? { fromCity: '', transitCity: '', toCity: '' } : null),
        ...(hotel ? { hotelOrVisa: '' } : null),
        ...(hotelCost ? { hotelCost: '', busSeats: '', busSeatCost: '' } : null),
      };
    });
  }, [f.bookingType]);

  const b = p.booking ?? null;

  /**
   * **سجل العميل يُكشف وقت كتابة الرقم لا وقت الحفظ** (قرار المالك).
   *
   * كان الخادم وحده يحرس التكرار: يرفض «جديد» لعميل له سجل عند الحفظ. وهو حارسٌ متأخّر —
   * البياع يملأ النموذج كلّه ثم يُقال له «هذا عميل قديم»، وقبلها لا شيء يقول له إن الرقم
   * الذي كتبه يعرفه النظام. فصار يُسأل `/customers/lookup` بمجرّد اكتمال الرقم، ويُعرض
   * الجواب تحت الخانة ومعه **رقم آخر حجز** ليرجع إليه.
   *
   * والقراءة **لحجز جديد وحده**: في «تفاصيل الحجز» يحمل الرقم سجلّ الحجز نفسه، فتحذيره
   * من نفسه ضجيج. وفشل القراءة صامت: هذا كشفٌ مساعد، والخادم يبقى الحارس الفاصل عند الحفظ.
   */
  const [history, setHistory] = useState<CustomerLookup | null>(null);
  const phoneForLookup = (p.lockedPhone ?? f.phone).replace(/[\s()-]/g, '');
  const nameForLookup = f.customerName.trim();
  // رقمٌ مكتمل (مصري ١١ رقمًا أو دولي)، أو اسمٌ من كلمتين فأكثر — أيّهما يكفي ليُسأل الخادم:
  // العميل المعروف باسمه لا ينتظر رقمًا صحيحًا ليُقال إنه معروف (قرار المالك، 0050).
  const phoneReady = /^(01\d{9}|\+\d{10,15})$/.test(phoneForLookup);
  const nameReady = nameForLookup.split(/\s+/).filter(Boolean).length >= 2;
  useEffect(() => {
    setHistory(null);
    if (b || p.readOnly) return;                               // حجز قائم: لا كشف
    if (!phoneReady && !nameReady) return;
    let alive = true;
    const t = setTimeout(() => {
      void (async () => {
        try {
          const params = [
            phoneReady ? `phone=${encodeURIComponent(phoneForLookup)}` : '',
            nameReady ? `name=${encodeURIComponent(nameForLookup)}` : '',
          ].filter(Boolean).join('&');
          const r = await get<CustomerLookup>(`/customers/lookup?${params}`);
          if (alive && r.exists) setHistory(r);
        } catch { /* كشفٌ مساعد: صمتُه لا يعطّل النموذج، والخادم يحرس عند الحفظ */ }
      })();
    }, 350);                                                   // انتظار توقّف الكتابة
    return () => { alive = false; clearTimeout(t); };
  }, [phoneForLookup, nameForLookup, phoneReady, nameReady, b, p.readOnly]);

  /**
   * عميلٌ معروف ⇒ العملية «مكرر» حتمًا (قرار المالك): تُضبط فور الكشف ولا يملك البياع
   * ردّها إلى «جديد» — «جديد» ترتفع من القائمة، والقاعدة ترفضها على كل حال.
   */
  useEffect(() => {
    if (history && f.operationType === 'NEW') set('operationType', 'REPEAT');
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [history]);

  /**
   * تاريخ ميلادٍ مسجَّلٌ عند الشركة يملأ الخانة تلقائيًا (0085).
   *
   * الخانة إجبارية بقرار المالك، وإلزامُ البياع بإعادة كتابة تاريخٍ **يعرفه النظام
   * أصلًا** هو ما يدفعه لكتابة أي تاريخٍ ليعدّي الشاشة — وذلك بعينه ما يُفسد حملة
   * التهنئة. **ولا يدهس ما كتبه البياع**: إن كانت الخانة مملوءة تُترك، فالتصحيح حقّه.
   */
  useEffect(() => {
    if (history?.dateOfBirth && !f.dateOfBirth) set('dateOfBirth', history.dateOfBirth);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [history]);

  const showCommission = !b?.isOwnerBooking;
  const isCancellation = f.operationType === 'CANCELLATION';
  // خطّ السير يظهر مع أنواع الطيران وحدها (ومنها «حجز مبدئى»)، ويختفي مع فندق/تأشيرة/بار كود.
  const showItinerary = hasFlightLeg(f.bookingType);
  // «شركة» وحدها تُظهر اسم الشركة وتُلزمه — والقاعدة تمنعه في غيرها (0088)
  const isCompany = f.operationType === 'COMPANY';
  // خانة الفندق/التأشيرة تظهر مع الأنواع التي فيها فندق أو تأشيرة وحدها، وتختفي مع الطيران
  // المجرّد والمبدئي وبار كود — فلا خانة بلا معنى تُملأ بـ«لا يوجد» في كل حجز.
  const showHotelOrVisa = hasHotelOrVisa(f.bookingType);
  // حجز الفندق: مكوّنات التكلفة بدل رقمٍ واحد — والمجموع معاينةٌ هنا، وحسابه الفاصل على الخادم
  const isHotel = hasHotel(f.bookingType);
  const hotelTotal: number | null = (() => {
    if (!isHotel || !isWholeEGP(f.hotelCost)) return null;
    const seats = f.busSeats.trim() ? Number(f.busSeats.trim()) : 0;
    const seatCost = f.busSeatCost.trim() && isWholeEGP(f.busSeatCost) ? Number(f.busSeatCost.trim()) : 0;
    return Number(f.hotelCost.trim()) + seats * seatCost;
  })();
  const disabled = p.readOnly || p.busy || resolving;

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!f.customerName.trim()) e.customerName = 'اسم العميل مطلوب';
    if (!p.lockedPhone && !f.phone.trim()) e.phone = 'رقم الهاتف مطلوب';
    // تاريخ الميلاد إجباري (0085، قرار المالك) — وحدّاه هنا هما حدّا الخادم والقاعدة
    // نفسهما، فيُقال الخطأ قبل الحفظ لا بعده. و«2062» بدل «1962» خطأُ إصبعٍ واحد،
    // ولا يُكتشف إلا يوم الحملة حين تُرسَل تهنئةٌ لا أحد ينتظرها.
    if (!f.dateOfBirth) e.dateOfBirth = 'تاريخ ميلاد العميل مطلوب';
    else if (f.dateOfBirth > cairoToday()) e.dateOfBirth = 'تاريخ ميلاد في المستقبل — راجع السنة';
    else if (f.dateOfBirth < '1900-01-01') e.dateOfBirth = 'تاريخ ميلاد قديم أكثر من اللازم — راجع السنة';
    if (!f.bookingType) e.bookingType = 'نوع الحجز مطلوب';
    /**
     * حقولٌ صارت **إجبارية** (قرار المالك: «مصدر التنفيذ خليه إجباري، ومن وإلى
     * إجباري، والجنسية إجباري»). ونفس شروط الخادم حرفيًا — فما تمنعه الشاشة يمنعه
     * الخادم، ولا يُقال للبياع «تمام» ثم يُردّ عليه.
     */
    if (!f.nationality.trim()) e.nationality = 'الجنسية مطلوبة';
    // مصدر التنفيذ: واحدٌ لغير المركّب، ولكل جزءٍ في المركّب (0124) — نفس شرط الخادم والقاعدة
    if (isCombined(f.bookingType)) {
      if (!f.executionSourceHotel) e.executionSourceHotel = 'مصدر تنفيذ الفندق مطلوب';
      if (!f.executionSourceFlight) e.executionSourceFlight = 'مصدر تنفيذ الطيران مطلوب';
      if (f.bookingType === 'HOTEL_FLIGHT_VISA' && !f.executionSourceVisa) e.executionSourceVisa = 'مصدر تنفيذ التأشيرة مطلوب';
    } else if (!f.executionSource) e.executionSource = 'مصدر التنفيذ مطلوب';
    // اسم الشركة: يلزم حجزَ الشركات وحدها — ونفس شرط الخادم والقاعدة (0088)
    if (isCompany && !f.companyName.trim()) e.companyName = 'اسم الشركة مطلوب';
    // و«من/إلى» بشرط ظهورهما: لا يُطلب ما لا يُعرض
    if (hasFlightLeg(f.bookingType)) {
      if (!f.fromCity.trim()) e.fromCity = '«من» مطلوبة لحجوزات الطيران';
      if (!f.toCity.trim()) e.toCity = '«الى» مطلوبة لحجوزات الطيران';
    }
    const [a, c, i] = [Number(f.adults || 0), Number(f.children || 0), Number(f.infants || 0)];
    for (const [k, v, lbl] of [['adults', a, BK.adults], ['children', c, BK.children], ['infants', i, BK.infants]] as const) {
      if (!Number.isInteger(v) || v < 0) e[k] = `${lbl}: عدد صحيح أكبر من أو يساوي صفر`;
    }
    if (!e.adults && !e.children && !e.infants && a + c + i < 1) e.adults = 'يجب أن يكون إجمالي الركاب شخصًا واحدًا على الأقل';
    if (!f.departureDate) e.departureDate = 'تاريخ الاقلاع / الدخول مطلوب';
    if (f.returnDate && f.departureDate && f.returnDate < f.departureDate) e.returnDate = 'تاريخ العوده لا يمكن أن يسبق تاريخ الاقلاع';
    if (!f.salePrice.trim()) e.salePrice = 'سعر البيع مطلوب';
    else if (!isWholeEGP(f.salePrice)) e.salePrice = MONEY_ERROR;
    if (hasHotel(f.bookingType)) {
      // حجز الفندق: التكلفة من مكوّناتها — تكلفة الفندق مطلوبة، والباص إمّا كاملٌ (عدد وسعر) أو غائب
      if (!f.hotelCost.trim()) e.hotelCost = 'سعر التكلفة للفندق مطلوب';
      else if (!isWholeEGP(f.hotelCost)) e.hotelCost = MONEY_ERROR;
      const seats = f.busSeats.trim(), seatCost = f.busSeatCost.trim();
      if (seats && !/^\d{1,3}$/.test(seats)) e.busSeats = 'عدد الكراسي رقم صحيح';
      if (seatCost && !isWholeEGP(seatCost)) e.busSeatCost = MONEY_ERROR;
      if (seats && !seatCost) e.busSeatCost = 'اكتب تكلفة الكرسي مع عدد الكراسي';
      if (seatCost && !seats) e.busSeats = 'اكتب عدد الكراسي مع تكلفة الكرسي';
    } else {
      if (!f.costPrice.trim()) e.costPrice = 'سعر التكلفة مطلوب';
      else if (!isWholeEGP(f.costPrice)) e.costPrice = MONEY_ERROR;
    }
    if (!f.operationType) e.operationType = 'تصنيف العميل مطلوب';
    if (needsRelation(f.operationType) && !f.relatedBookingNo.trim() && !f.externalReference.trim()) {
      e.relatedBookingNo = 'سجل التعديل/الإلغاء يجب ربطه برقم الحجز الأصلي أو بمرجع خارجي (PNR)';
    }
    return e;
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length) return;

    let relatedBookingId: string | null = null;
    if (needsRelation(f.operationType) && f.relatedBookingNo.trim()) {
      setResolving(true);
      try {
        const r = await get<{ items: Booking[] }>(`/bookings${qs({ bookingNo: f.relatedBookingNo.trim(), limit: 1 })}`);
        relatedBookingId = r.items[0]?.id ?? null;
        if (!relatedBookingId) { setErrors({ relatedBookingNo: 'لم يتم العثور على حجز بهذا الرقم' }); return; }
      } catch {
        setErrors({ relatedBookingNo: 'تعذّر التحقق من رقم الحجز الأصلي' }); return;
      } finally { setResolving(false); }
    }

    const body: Record<string, unknown> = {
      customerName: f.customerName.trim(),
      dateOfBirth: f.dateOfBirth,
      // يُرسَل فارغًا في غير الشركات: القاعدة تمنع بقاءه محفوظًا لو بُدِّل النوع
      companyName: isCompany ? f.companyName.trim() : null,
      bookingType: f.bookingType,
      hotelOrVisa: f.hotelOrVisa.trim() || null,
      nationality: f.nationality.trim() || null,
      fromCity: f.fromCity.trim() || null,
      transitCity: f.transitCity.trim() || null,
      toCity: f.toCity.trim() || null,
      adults: Number(f.adults), children: Number(f.children), infants: Number(f.infants),
      departureDate: f.departureDate,
      returnDate: f.returnDate || null,
      salePrice: f.salePrice.trim(),
      // حجز الفندق: تُرسل المكوّنات، والخادم يحسب «سعر التكلفة» منها (trigger 0051) — وما
      // يُرسل هنا باسم costPrice في الفندق مجرّد قيمة أوّلية يستبدلها المجموع على الخادم
      ...(hasHotel(f.bookingType)
        ? {
            costPrice: String(hotelTotal ?? 0),
            hotelCost: f.hotelCost.trim(),
            busSeats: f.busSeats.trim() ? Number(f.busSeats.trim()) : null,
            busSeatCost: f.busSeatCost.trim() || null,
          }
        : { costPrice: f.costPrice.trim(), hotelCost: null, busSeats: null, busSeatCost: null }),
      // المركّب يرسل مصادر أجزائه ولا يرسل الواحد (الخادم يجعل القديم = مصدر الفندق)؛ وغيرُه العكس
      ...(isCombined(f.bookingType)
        ? {
            executionSource: null,
            executionSourceHotel: f.executionSourceHotel || null,
            executionSourceFlight: f.executionSourceFlight || null,
            executionSourceVisa: f.bookingType === 'HOTEL_FLIGHT_VISA' ? (f.executionSourceVisa || null) : null,
          }
        : { executionSource: f.executionSource || null, executionSourceHotel: null, executionSourceFlight: null, executionSourceVisa: null }),
      customerRating: f.customerRating || null,
      operationType: f.operationType,
    };
    if (!p.lockedPhone) body.phone = f.phone.trim();
    body.altPhone = f.altPhone.trim() || null;            // رقمٌ تانٍ اختياري (0183)
    if (needsRelation(f.operationType)) {
      body.relatedBookingId = relatedBookingId;
      body.externalReference = f.externalReference.trim() || null;
    }
    await p.onSubmit(body);
  }

  const bookingTypes = useMemo(() => (p.lookups.bookingTypes ?? []).map(opt), [p.lookups.bookingTypes]);
  /**
   * **«iCon ( حجز مبدئى )»** (0129، قرار المالك 26/09/2026: «فى حالة نوع الحجز حجز مبدئى , خلى ف مصدر تنفيذ
   * اسمه iCon ( حجز مبدئى )»): يظهر مع «حجز مبدئى» وحده — والقاعدة ترفضه على غيره.
   */
  const sources = useMemo(() => [
    ...[...(p.lookups.executionSources ?? []), ...extraSources].filter(x => x.code !== ICON_PRELIMINARY || f.bookingType === PRELIMINARY).map(opt),
    ...(owner ? [{ code: NEW_SOURCE, ar: '➕ مصدر جديد…' }] : []),
  ], [p.lookups.executionSources, extraSources, owner, f.bookingType]);
  const ratings = useMemo(() => (p.lookups.customerRatings ?? []).map(opt), [p.lookups.customerRatings]);

  return (
    <form onSubmit={submit} aria-label={p.formLabel} className="space-y-3">
      {p.error && <Alert>{p.error}</Alert>}
      {p.banner}
      {p.readOnly && p.readOnlyNote && <Alert kind="info">{p.readOnlyNote}</Alert>}

      {/* الخانات على سطرٍ واحد بصريًا (قرار المالك): عنوانٌ يلتفّ سطرين — «تاريخ الاقلاع /
          الدخول» — كان يدفع خانته لأسفل فتبدو ناشزة عن جاراتها. وحدُّ أدنى لارتفاع العنوان
          يجعل كل الخانات في الصفّ تبدأ من مستوى واحد مهما طال عنوانها، والمسافات أضيق
          رأسيًا حتى يرى البياع النموذج كلّه بلا تمرير. */}
      <div className="grid gap-x-3 gap-y-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6
                      [&_.control]:py-2 [&_.label]:mb-0.5 [&_.label]:min-h-[2.75em]">
        {/* 1 */}
        <Field label={BK.salesName} hint="يُملأ تلقائيًا من المستخدم المسجَّل">
          <TextInput value={p.salesName} readOnly aria-readonly="true" className="bg-slate-50 text-ink-700" />
        </Field>
        {/* 2 */}
        <Field label={BK.customerName} required error={errors.customerName}>
          <TextInput ref={first} value={f.customerName} disabled={disabled} onChange={e => set('customerName', e.target.value)} />
        </Field>
        {/* 3 */}
        {/* يُكتب ويُعرض بالشكل المصري المحلي (01033720296). والتطبيع إلى E.164 يقع في
            الخادم وحده عند الحفظ — فالمخزَّن موحَّد والمعروض مألوف. */}
        <Field label={BK.phone} required={!p.lockedPhone} error={errors.phone}
          hint={p.lockedPhone ? 'من المتابعة — يُحفظ كما هو' : 'مثال: 01033720296'}>
          <TextInput dir="ltr" inputMode="tel"
            value={p.lockedPhone ? displayPhone(p.lockedPhone) : f.phone}
            readOnly={!!p.lockedPhone} aria-readonly={p.lockedPhone ? 'true' : undefined}
            disabled={disabled && !p.lockedPhone} onChange={e => set('phone', e.target.value)}
            className={p.lockedPhone ? 'bg-slate-50 text-ink-700' : ''} />
        </Field>
        <AltPhoneField value={f.altPhone} disabled={disabled} onChange={v => set('altPhone', v)} />
        {/* تاريخ ميلاد العميل (0085) — بجانب اسمه وتليفونه لأنه من بيانات **الإنسان**
            لا من بيانات الصفقة، وخارج ترتيب الشيت 1→22 عمدًا (انظر `fields.ts`).
            ويظهر في كل أنواع الحجز بلا استثناء (قرار المالك). */}
        <Field label={BK.dateOfBirth} required error={errors.dateOfBirth}
          hint={history?.dateOfBirth ? 'مسجَّل عندنا من قبل — عدّله لو غلط' : 'هيتبعتله تهنئة في عيد ميلاده'}>
          <DateInput value={f.dateOfBirth} disabled={disabled} onChange={v => set('dateOfBirth', v)} />
        </Field>
        {/* سجل العميل فور اكتمال الرقم: يُقال إنه مسجَّل، ومعه رقم آخر حجز ليرجع إليه،
            وزرٌّ يحوّل العملية إلى «مكرر» — فلا يُكتشف التكرار بعد ملء النموذج كلّه. */}
        {history && (
          <div role="status" aria-live="polite" aria-label="سجل العميل"
            className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 sm:col-span-2 lg:col-span-3 xl:col-span-4">
            <strong>عميل مسجَّل بالفعل</strong>
            {/* عميل بياعٍ آخر: جملة الأمانة وحدها — بلا تفاصيله (قرار المالك 02/10/2026) */}
            {history.ownedByOther && history.notice ? <> — {history.notice}</> : <>
            {history.previousName ? ` — ${history.previousName}` : ''}
            {history.bookingsCount !== null ? ` · ${history.bookingsCount} حجز سابق` : ''}
            {history.lastBookingNo ? ` · آخرها ${history.lastBookingNo}` : ''}
            {history.lastBookingDate ? ` بتاريخ ${shortDate(history.lastBookingDate)}` : ''}
            {/* من أغلق العملية معه: يعرف البياع إلى من يرجع في تفاصيل ما جرى (قرار المالك) */}
            {history.lastSellerName ? ` · البياع: ${history.lastSellerName}` : ''}
            </>}
            {/* والعملية صارت «مكرر» بنفسها: لا زرّ ولا اختيار (قرار المالك) */}
            {' — العملية «مكرر» إلزامًا.'}
          </div>
        )}
        {/* 4 */}
        <Field label={BK.bookingType} required error={errors.bookingType}>
          <Select options={bookingTypes} value={f.bookingType} disabled={disabled} onChange={e => set('bookingType', e.target.value)} />
        </Field>
        {/* 5 — مع أنواع الفندق والتأشيرة وحدها */}
        {showHotelOrVisa && (
          <Field label={BK.hotelOrVisa}>
            <div className="flex gap-1">
              <TextInput value={f.hotelOrVisa} disabled={disabled} onChange={e => set('hotelOrVisa', e.target.value)} />
              <button type="button" className="btn-ghost shrink-0 px-2 text-xs" disabled={disabled} onClick={() => set('hotelOrVisa', 'لا يوجد')}>لا يوجد</button>
            </div>
          </Field>
        )}
        {/* 6 — نصّ حرّ: لا قائمة جنسيات ثابتة، والحقل يبدأ فارغًا (قرار المالك) */}
        <Field label={BK.nationality} required error={errors.nationality}>
          <TextInput value={f.nationality} disabled={disabled} onChange={e => set('nationality', e.target.value)} />
        </Field>
        {/* 7 · 8 · 9 — خطّ السير: مع أنواع الطيران وحدها */}
        {showItinerary && (
          <>
            <Field label={BK.fromCity} required error={errors.fromCity}>
              <TextInput value={f.fromCity} disabled={disabled} onChange={e => set('fromCity', e.target.value)} />
            </Field>
            <Field label={BK.transitCity}>
              <div className="flex gap-1">
                <TextInput value={f.transitCity} disabled={disabled}
                  onChange={e => set('transitCity', e.target.value)} />
                <button type="button" className="btn-ghost shrink-0 px-2 text-xs" disabled={disabled}
                  onClick={() => set('transitCity', 'لا يوجد')}>لا يوجد</button>
              </div>
            </Field>
            <Field label={BK.toCity} required error={errors.toCity}>
              <TextInput value={f.toCity} disabled={disabled} onChange={e => set('toCity', e.target.value)} />
            </Field>
          </>
        )}
        {/* 10 */}
        <Field label={BK.adults} required error={errors.adults}>
          <TextInput type="number" min={0} max={999} inputMode="numeric" value={f.adults} disabled={disabled} onChange={e => set('adults', e.target.value)} />
        </Field>
        {/* 11 */}
        <Field label={BK.children} error={errors.children}>
          <TextInput type="number" min={0} max={999} inputMode="numeric" value={f.children} disabled={disabled} onChange={e => set('children', e.target.value)} />
        </Field>
        {/* 12 */}
        <Field label={BK.infants} error={errors.infants}>
          <TextInput type="number" min={0} max={999} inputMode="numeric" value={f.infants} disabled={disabled} onChange={e => set('infants', e.target.value)} />
        </Field>
        {/* 13 */}
        <Field label={BK.departureDate} required error={errors.departureDate}>
          {/* يوم/شهر/سنة كبقيّة النظام — لا بصيغة المتصفّح (قرار المالك) */}
          <DateInput value={f.departureDate} disabled={disabled} onChange={v => set('departureDate', v)} />
        </Field>
        {/* 14 */}
        <Field label={BK.returnDate} error={errors.returnDate} hint="اتركه فارغًا لرحلة ذهاب فقط">
          <DateInput value={f.returnDate} disabled={disabled} onChange={v => set('returnDate', v)} />
        </Field>
        {/* 15 */}
        {/* الإلغاء (قاعدة معتمدة، Phase 0 §5.3): البيع = المسترد من الطيران، والتكلفة = المرتجع للعميل.
            المعنى المالي لا يتغيّر — الحقلان نفسهما — والعنوان يقوله صراحةً حين يختار البياع «الغاء». */}
        <Field label={isCancellation ? `${BK.salePrice} — المسترد من الطيران` : partsLabel(BK.salePrice, f.bookingType)} required error={errors.salePrice}>
          <TextInput inputMode="numeric" className="money-input" placeholder="0" value={f.salePrice} disabled={disabled} onChange={e => set('salePrice', e.target.value)} />
        </Field>
        {/* 16 — في حجز الفندق (قرار المالك، 0051): تكلفة الفندق + كراسي الباص × تكلفة الكرسي =
            التكلفة الإجمالية، وهي «سعر التكلفة» نفسه قبل ضريبة الـ٣٪. الجمع على الخادم؛ وما يُعرض
            هنا معاينة لا تُرسل. وفي غير الفندق يبقى «سعر التكلفة» رقمًا واحدًا كما كان. */}
        {isHotel ? (
          <>
            <Field label={partsLabel(BK.hotelCost, f.bookingType)} required error={errors.hotelCost}>
              <TextInput inputMode="numeric" className="money-input" placeholder="0" value={f.hotelCost} disabled={disabled} onChange={e => set('hotelCost', e.target.value)} />
            </Field>
            <Field label={BK.busSeats} error={errors.busSeats} hint="اتركه فارغًا إن لم يحجز انتقالات">
              <TextInput type="number" min={0} max={999} inputMode="numeric" value={f.busSeats} disabled={disabled} onChange={e => set('busSeats', e.target.value)} />
            </Field>
            <Field label={BK.busSeatCost} error={errors.busSeatCost}>
              <TextInput inputMode="numeric" className="money-input" placeholder="0" value={f.busSeatCost} disabled={disabled} onChange={e => set('busSeatCost', e.target.value)} />
            </Field>
            <Computed label={BK.totalCost}
              value={hotelTotal === null ? null : String(hotelTotal)}
              hint="الفندق + الكراسي × تكلفة الكرسي — قبل ضريبة الـ٣٪، ويُحسب على الخادم" />
          </>
        ) : (
          <Field label={isCancellation ? `${BK.costPrice} — المرتجع للعميل` : BK.costPrice} required error={errors.costPrice}>
            <TextInput inputMode="numeric" className="money-input" placeholder="0" value={f.costPrice} disabled={disabled} onChange={e => set('costPrice', e.target.value)} />
          </Field>
        )}
        {/* 17 */}
        <Computed label={BK.costWithTax} value={b?.costWithTax} hint="يُحسب على الخادم" />
        {/* 18 + 19 — لا تظهران في حجوزات المالك (بلا عمولة) */}
        {showCommission && <Computed label={BK.commissionRatePct} value={b?.commissionRatePct} suffix="%" hint="نسبة مبدئية حتى التسوية الشهرية" />}
        {showCommission && <Computed label={BK.provisionalCommission} value={b?.provisionalCommission} hint="عمولة مبدئية (قبل التسوية الشهرية)" />}
        {/* 20 — واحدٌ لغير المركّب، وخانةٌ لكل جزءٍ في المركّب (0124، قرار المالك 23/09/2026) */}
        {isCombined(f.bookingType) ? (
          <>
            <Field label={BK.executionSourceHotel} required error={errors.executionSourceHotel}>
              <Select options={sources} placeholder="—" value={addingFor === 'executionSourceHotel' ? NEW_SOURCE : f.executionSourceHotel} disabled={disabled} onChange={e => pickSource('executionSourceHotel', e.target.value)} />
              {newSourceBox('executionSourceHotel')}
            </Field>
            <Field label={BK.executionSourceFlight} required error={errors.executionSourceFlight}>
              <Select options={sources} placeholder="—" value={addingFor === 'executionSourceFlight' ? NEW_SOURCE : f.executionSourceFlight} disabled={disabled} onChange={e => pickSource('executionSourceFlight', e.target.value)} />
              {newSourceBox('executionSourceFlight')}
            </Field>
            {f.bookingType === 'HOTEL_FLIGHT_VISA' && (
              <Field label={BK.executionSourceVisa} required error={errors.executionSourceVisa}>
                <Select options={sources} placeholder="—" value={addingFor === 'executionSourceVisa' ? NEW_SOURCE : f.executionSourceVisa} disabled={disabled} onChange={e => pickSource('executionSourceVisa', e.target.value)} />
              {newSourceBox('executionSourceVisa')}
              </Field>
            )}
          </>
        ) : (
          <Field label={BK.executionSource} required error={errors.executionSource}>
            <Select options={sources} placeholder="—" value={addingFor === 'executionSource' ? NEW_SOURCE : f.executionSource} disabled={disabled} onChange={e => pickSource('executionSource', e.target.value)} />
              {newSourceBox('executionSource')}
          </Field>
        )}
        {/* 21 — **لا يُملأ باليد** (قرار المالك): كل حجزٍ يبدأ «لم يتم»، ولا يصير «تم» إلا
            برفع صورة التقييم من تبويب «تقييم Google Map» — يفعلها المسار نفسه بلا تدخّل. */}
        <Field label={BK.customerRating} hint="يتغيّر وحده إلى «تم» عند رفع صورة تقييم Google Map">
          <Select options={ratings} placeholder="—" value={f.customerRating} disabled onChange={() => {}} />
        </Field>
        {/* 22 */}
        {/* **عميلٌ معروف عمليته «مكرر» حتمًا** (قرار المالك): «جديد» تُرفع من القائمة فلا
            تُختار أصلًا، والقاعدة تمنعها على كل حال (0050). وبقيّة الأنواع تبقى: «تعديل»
            و«إلغاء» و«شركة» عملياتٌ على عميلٍ قائم أصلًا، ومنعها يغلق بابها كلّه. */}
        <Field label={BK.operationType} required error={errors.operationType}
          hint={history ? 'عميل مسجَّل: «جديد» غير متاحة' : 'نوع العملية'}>
          <Select options={history ? OPERATION_TYPES.filter(o => o.code !== 'NEW') : OPERATION_TYPES}
            value={f.operationType} disabled={disabled} onChange={e => set('operationType', e.target.value)} />
        </Field>
        {/**
          * اسم الشركة (0088، قرار المالك: «لو مختار شركة… يكتب اسم الشركة»).
          *
          * **ومع «شركة» وحدها**: في حجز الشركات اثنان لا واحد — من سافر ومن يدفع.
          * فخانةُ «اسم العميل» تبقى للمسافر، وهذه للشركة. والقاعدة تمنع كتابتها في
          * غير حجز الشركات، فلا تبقى خانةٌ مملوءة في حجزٍ لا شركة فيه.
          */}
        {isCompany && (
          <Field label={BK.companyName} required error={errors.companyName}
            hint="الشركة اللي هتدفع — غير اسم المسافر نفسه">
            <TextInput value={f.companyName} disabled={disabled} maxLength={150}
              onChange={e => set('companyName', e.target.value)} />
          </Field>
        )}
      </div>

      {needsRelation(f.operationType) && (
        <fieldset className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
          <legend className="px-1 text-sm font-medium text-amber-900">الحجز الأصلي المرتبط</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="رقم الحجز الأصلي" error={errors.relatedBookingNo}>
              <TextInput dir="ltr" placeholder="BK-2026-000001" value={f.relatedBookingNo} disabled={disabled} onChange={e => set('relatedBookingNo', e.target.value)} />
            </Field>
            <Field label="مرجع خارجي (PNR)">
              <TextInput dir="ltr" value={f.externalReference} disabled={disabled} onChange={e => set('externalReference', e.target.value)} />
            </Field>
          </div>
        </fieldset>
      )}

      {p.isAdminUser && (
        <fieldset className="rounded-lg border border-brand-200 bg-brand-50/50 p-3">
          <legend className="px-1 text-sm font-medium text-brand-700">بيانات المالك فقط</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <Computed label={BK.grossProfitMarginPct} value={b?.grossProfitMarginPct} suffix="%" />
            <Computed label={BK.grossProfit} value={b?.grossProfit} />
          </div>
        </fieldset>
      )}

      {typeof p.footer === 'function' ? p.footer(f) : p.footer}

      {!p.readOnly && (
        <div className="flex flex-wrap gap-2">
          <button type="submit" className="btn-primary" disabled={p.busy || resolving}>{p.busy ? 'جارٍ الحفظ…' : (typeof p.submitLabel === 'function' ? p.submitLabel(f) : p.submitLabel)}</button>
          {p.extraActions}
        </div>
      )}
    </form>
  );
}
